import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { EndorsementData } from '@domain/types/entity';
import { zodResolver } from '@hookform/resolvers/zod';

import type { EndorsementFormState } from '../types';
import { EndorsementStep } from '../types';
import { getInitialValues } from '../utils';
import {
  createEndorsementSelectionSchema,
  createEndorsementStep0Schema,
  createEndorsementStep1Schema,
  createEndorsementStep2Schema,
  createEndorsementStep3Schema,
  validationSchema,
} from '../validation/schema';

// currentStep isn't known until after the stepper (which itself depends on formValues
// to decide whether the duplicate-endorsement step exists) is initialised, so per-step
// validity is computed separately, once the caller has both pieces - see getIsCurrentStepValid.
export function useEndorsementForm(entity?: EndorsementData, isEdit = false) {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
    setValue,
    watch,
  } = useForm<EndorsementFormState>({
    defaultValues: getInitialValues(entity, isEdit),
    resolver: zodResolver(validationSchema),
    mode: 'onTouched',
  });

  const formValues = watch();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'premium_change' && value.premium_change) {
        setValue('additional_premium', null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  return {
    control,
    formValues,
    handleSubmit,
    isValid,
    reset,
    setValue,
  };
}

export function getIsCurrentStepValid(
  formValues: EndorsementFormState,
  currentStep: EndorsementStep,
  isStandalone: boolean,
  isValid: boolean,
): boolean {
  if (isStandalone && currentStep === EndorsementStep.ParentSelection) {
    return createEndorsementStep0Schema.safeParse(formValues).success;
  }
  if (currentStep === EndorsementStep.SummaryTemplate) {
    return createEndorsementStep1Schema.safeParse(formValues).success;
  }
  if (currentStep === EndorsementStep.EndorsementSelection) {
    return createEndorsementSelectionSchema.safeParse(formValues).success;
  }
  if (currentStep === EndorsementStep.BasicInfo) {
    return createEndorsementStep2Schema.safeParse(formValues).success;
  }
  if (currentStep === EndorsementStep.ReasonPremium) {
    return createEndorsementStep3Schema.safeParse(formValues).success;
  }
  return isValid;
}
